<script setup lang="ts">
import { ref, computed, watch } from "vue";
import {
  equipmentFormKey,
  equipmentKey,
  parametersFormKey,
  scenarioCalculationsKey,
  scenarioFormKey,
  sizingFormKey,
  wellPropertiesKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { injectStrict, isNullOrUndefined } from "@/utils/utils";
import { useStorage } from "@vueuse/core";
import LabelledToggle from "@/components/LabelledToggle.vue";
import ScenarioEquipmentSchematic from "@/components/schematic/ScenarioEquipmentSchematic.vue";
import CrossSectionChartsSet from "@/components/scenarios/charts/CrossSectionChartsSet.vue";
import DeviationSurveyBendingStressAnalysis from "@/components/scenarios/charts/DeviationSurveyBendingStressAnalysis.vue";
import { scenarioHasValidEquipmentBendingStressCalcs } from "@/libs/charting/deviationBendingStress/validation";
import { BENDING_STRESS_PLACEMENTS } from "@/libs/charting/deviationBendingStress/useDeviationBendingStressChartData";
import {
  Deviation_Survey_Directional_Dataset_Enum,
  Sizing_Pumping_Type_Enum
} from "gql/api";

const scenarioForm = injectStrict(scenarioFormKey);
const equipmentForm = injectStrict(equipmentFormKey);
const parametersForm = injectStrict(parametersFormKey);
const equipment = injectStrict(equipmentKey);
const sizingForm = injectStrict(sizingFormKey, null);
const calcs = injectStrict(scenarioCalculationsKey);
const wellProperties = injectStrict(wellPropertiesKey);

const isEspOnShore = computed(() => {
  return (
    sizingForm?.data.value.pumping_type === Sizing_Pumping_Type_Enum.EspOnShore
  );
});

const scenario = computed(() => ({
  ...scenarioForm.data.value,
  equipment: equipmentForm.data.value,
  parameters: parametersForm.data.value,
  well_properties: wellProperties.value // Makes well properties reactive on the charts as well
}));

const scale = useStorage("schematicScale", 1.0);
const sagdMode = ref(false);

// View selection state
const selectedView = ref("Schematic"); // Default to Schematic view

// Chart settings
const chartMinHeight = ref(400);

const deviationSurvey = computed(
  () => scenario.value?.well_properties?.r_deviation_survey
);

const is3dSurvey = computed(
  () =>
    deviationSurvey.value?.directional_data_set ===
      Deviation_Survey_Directional_Dataset_Enum.ThreeDMdInclinationAzimuth &&
    scenario.value.well_properties?.deviation_survey_id !== null
);

const hasValidBendingStressChart = ref(true);

const refreshValid = () => {
  const hasSurvey =
    is3dSurvey.value ||
    !isNullOrUndefined(scenario.value.well_properties?.average_dls);
  if (!hasSurvey) {
    hasValidBendingStressChart.value = false;
    return;
  }

  hasValidBendingStressChart.value =
    scenarioHasValidEquipmentBendingStressCalcs(scenario.value, calcs);
};

watch([scenario, is3dSurvey, deviationSurvey], refreshValid, {
  immediate: true,
  deep: true
});
</script>

<template>
  <section class="container">
    <!-- Tab navigation -->
    <div class="schematic-header">
      <div class="view-tabs">
        <button
          class="p-button schematic-button"
          :class="{
            'p-button-outlined': selectedView === 'Schematic',
            'p-button-text': selectedView !== 'Schematic'
          }"
          @click="selectedView = 'Schematic'"
        >
          Schematic
        </button>

        <button
          v-if="isEspOnShore"
          class="p-button analysis-button"
          :class="{
            'p-button-outlined': selectedView === 'Cross Sectional Analysis',
            'p-button-text': selectedView !== 'Cross Sectional Analysis'
          }"
          @click="selectedView = 'Cross Sectional Analysis'"
        >
          Cross Sectional Analysis
        </button>
        <button
          v-if="isEspOnShore"
          class="p-button analysis-button"
          :class="{
            'p-button-outlined': selectedView === 'Bending Stress Analysis',
            'p-button-text': selectedView !== 'Bending Stress Analysis'
          }"
          @click="selectedView = 'Bending Stress Analysis'"
        >
          Bending Stress Analysis
        </button>
      </div>

      <!-- SAGD Mode toggle and zoom controls - only shown in Schematic view -->
      <div v-if="selectedView === 'Schematic'" class="controls">
        <LabelledToggle
          v-if="isEspOnShore"
          v-model="sagdMode"
          text="SAGD Mode"
        />
        <span
          class="my-2 body-1 flex justify-content-between align-items-center"
        >
          zoom <span class="numeral-2 ml-2 mr-2">{{ scale }}</span>
          <input
            v-model.number="scale"
            type="range"
            :step="0.01"
            :max="2"
            :min="0.2"
          />
          <button
            class="p-button p-button-sm p-button-text body-1"
            @click="scale = 1.0"
          >
            reset
          </button>
        </span>
      </div>
    </div>

    <!-- View content based on selected tab -->
    <div
      v-if="selectedView === 'Schematic' && scenario && calcs"
      class="view-content"
    >
      <ScenarioEquipmentSchematic
        :scenario="scenario"
        :scenario-calculations="calcs"
        :sagd-mode="sagdMode"
        :style="`zoom: ${scale}`"
      />
    </div>

    <div
      v-if="selectedView === 'Cross Sectional Analysis' && calcs"
      class="view-content"
    >
      <CrossSectionChartsSet
        title="Equipment Cross Sections"
        :scenario-calculations="calcs"
        :equipment="equipment"
        :well-properties="wellProperties"
        enable-mouse-tracking
        :minimum-height-px="chartMinHeight"
        :aspect-ratio="1"
        equipment-set="both"
      />
    </div>
    <div
      v-if="selectedView === 'Bending Stress Analysis' && calcs"
      class="view-content"
    >
      <h2 class="dashboard-title">Bending Stress Analysis</h2>
      <DeviationSurveyBendingStressAnalysis
        v-for="placement in BENDING_STRESS_PLACEMENTS"
        :key="placement"
        :placement="placement"
        :draw-legend="true"
        :draw-equipment-labels="false"
        :scenario-calculations="calcs"
        :scenario="scenario"
        :entry-rows="
          hasValidBendingStressChart ? calcs.entryRows.value.data : []
        "
        enable-mouse-tracking
        :minimum-height-px="chartMinHeight"
        :aspect-ratio="5"
      />
    </div>
  </section>
</template>

<style lang="scss">
.dashboard-title {
  justify-self: center;
}
.schematic {
  background-color: white;
  margin: 0 auto;
  max-height: 85vh;
}

.schematic-header {
  padding-top: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 900px;
  margin: 0 auto;
  min-height: 100px;
}

html[data-theme="dark"] .schematic {
  background-color: black;
}

.view-tabs {
  display: flex;
  gap: 0.5rem;
}

@media only screen and (max-width: 766px) {
  .schematic-header {
    flex-direction: column;
    width: 100%;
    margin-bottom: 0;
  }
  .controls {
    width: 100%;
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
  }
  .view-tabs {
    display: inline-block;
  }
  .view-content {
    margin-top: 10px;
  }
}
</style>
